
import React, { useState, useEffect } from 'react';
import { trpc } from '@/trpc/client';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { useSeason } from '@/context/SeasonContext';

const AdminSettingsPage = () => {
    const { refetchSettings } = useSeason();

    // Fetch current via public query (could also add a specific admin Get)
    const { data, isLoading, refetch } = trpc.system.getPublicSettings.useQuery();

    // State for Maintenance Mode
    const [isMaintenanceMode, setIsMaintenanceMode] = useState(false);
    const [maintenanceStart, setMaintenanceStart] = useState<string>('');
    const [maintenanceEnd, setMaintenanceEnd] = useState<string>('');

    // State for Seasonal Mode
    const [isAutoMode, setIsAutoMode] = useState(true);
    const [selectedSeason, setSelectedSeason] = useState<string>('none');

    useEffect(() => {
        if (data) {
            setIsAutoMode(data.seasonMode === 'auto');
            setSelectedSeason(data.currentSeason);

            // Maintenance values
            setIsMaintenanceMode(data.maintenanceMode === 'true');
            setMaintenanceStart(data.maintenanceStart || '');
            setMaintenanceEnd(data.maintenanceEnd || '');
        }
    }, [data]);

    const updateSettingMutation = trpc.system.updateSystemSetting.useMutation({
        onSuccess: () => {
            // Refetch both local data and global context
            refetch();
            refetchSettings();
            toast.success('Settings updated successfully');
        },
        onError: (err) => toast.error(err.message)
    });

    const handleSave = async () => {
        try {
            await updateSettingMutation.mutateAsync({
                key: 'season_mode',
                value: isAutoMode ? 'auto' : 'manual'
            });

            if (!isAutoMode) {
                await updateSettingMutation.mutateAsync({
                    key: 'current_season',
                    value: selectedSeason
                });
            }
        } catch (e) {
            // Error handled in mutation
        }
    };

    const handleMaintenanceSave = async () => {
        try {
            await updateSettingMutation.mutateAsync({
                key: 'maintenance_mode',
                value: String(isMaintenanceMode)
            });

            if (isMaintenanceMode) {
                if (maintenanceStart) {
                    await updateSettingMutation.mutateAsync({
                        key: 'maintenance_start',
                        value: maintenanceStart
                    });
                }
                if (maintenanceEnd) {
                    await updateSettingMutation.mutateAsync({
                        key: 'maintenance_end',
                        value: maintenanceEnd
                    });
                }
            }
        } catch (e) {
            // Error handled in mutation
        }
    };

    return (
        <div className="container mx-auto py-6 px-4 animate-fade-in">
            <header className="mb-8">
                <h1 className="text-3xl font-bold">System Settings</h1>
                <p className="text-muted-foreground">Manage global application configurations.</p>
            </header>

            <div className="grid gap-6">
                {/* Maintenance Mode Card */}
                <Card className="glass max-w-2xl border-destructive/20 shadow-red-500/5">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <Loader2 className={`h-5 w-5 ${isMaintenanceMode ? 'text-destructive animate-pulse' : 'text-muted-foreground'}`} />
                            Maintenance Mode
                        </CardTitle>
                        <CardDescription>
                            Schedule system downtime. Users (except Admins) will be blocked from accessing the platform.
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-6">
                        <div className="flex items-center justify-between space-x-2">
                            <div className="flex flex-col space-y-1">
                                <Label htmlFor="maintenance-mode" className="font-medium text-destructive">Enable Maintenance</Label>
                                <span className="text-sm text-muted-foreground">System becomes inactive for non-admins.</span>
                            </div>
                            <Switch
                                id="maintenance-mode"
                                checked={isMaintenanceMode}
                                onCheckedChange={setIsMaintenanceMode}
                            />
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>Start Time</Label>
                                <input
                                    type="datetime-local"
                                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                                    value={maintenanceStart}
                                    onChange={(e) => setMaintenanceStart(e.target.value)}
                                    disabled={!isMaintenanceMode}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>End Time</Label>
                                <input
                                    type="datetime-local"
                                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                                    value={maintenanceEnd}
                                    onChange={(e) => setMaintenanceEnd(e.target.value)}
                                    disabled={!isMaintenanceMode}
                                />
                            </div>
                        </div>

                        <Button
                            variant="destructive"
                            onClick={handleMaintenanceSave}
                            disabled={updateSettingMutation.isLoading}
                            className="w-full sm:w-auto"
                        >
                            {updateSettingMutation.isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                            Update Maintenance Schedule
                        </Button>
                    </CardContent>
                </Card>

                <Card className="glass max-w-2xl">
                    <CardHeader>
                        <CardTitle>Seasonal Effects</CardTitle>
                        <CardDescription>Control the global seasonal theme (Snow, Halloween, etc.).</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-6">
                        {isLoading ? (
                            <Loader2 className="animate-spin" />
                        ) : (
                            <>
                                <div className="flex items-center justify-between space-x-2">
                                    <div className="flex flex-col space-y-1">
                                        <Label htmlFor="auto-mode" className="font-medium">Auto Mode</Label>
                                        <span className="text-sm text-muted-foreground">Automatically select season based on date.</span>
                                    </div>
                                    <Switch
                                        id="auto-mode"
                                        checked={isAutoMode}
                                        onCheckedChange={setIsAutoMode}
                                    />
                                </div>

                                <div className="space-y-2">
                                    <Label className={isAutoMode ? "text-muted-foreground" : ""}>Select Season</Label>
                                    <Select
                                        disabled={isAutoMode}
                                        value={selectedSeason}
                                        onValueChange={setSelectedSeason}
                                    >
                                        <SelectTrigger>
                                            <SelectValue placeholder="Select a season" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="none">None (Default)</SelectItem>
                                            <SelectItem value="winter">Winter / Christmas (❄️)</SelectItem>
                                            <SelectItem value="halloween">Halloween (🎃)</SelectItem>
                                            <SelectItem value="valentine">Valentine's Day (❤️)</SelectItem>
                                            <SelectItem value="stpats">St. Patrick's Day (☘️)</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>

                                <Button
                                    onClick={handleSave}
                                    disabled={updateSettingMutation.isLoading}
                                    className="w-full sm:w-auto"
                                >
                                    {updateSettingMutation.isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                                    Save Changes
                                </Button>
                            </>
                        )}
                    </CardContent>
                </Card>
            </div>
        </div>
    );
};

export default AdminSettingsPage;
